import React from "react";
import { Grid } from "Components/Base/Layout/Grid";
import { Typography } from "Components/Base/DataDisplay/Typography";
import ItemIcon from "./ItemIcon";
import useMobileViewportCheck from "../../../Portal/web/Content/js/lib/effects/useMobileViewportCheck";
import "./style.css";

/**
 * A list item component for displaying learning activities
 * @param {string} props.id - Unique identifier for the activity
 * @param {string} props.title - The title of the activity
 * @param {string} props.type - The type of activity
 * @param {React.ReactNode} props.chips - Chip components to display
 * @param {React.ReactNode} props.content - The main content of the activity
 * @param {React.ReactNode} props.actions - Action components to display
 * @param {boolean} [props.displayImage=false] - Whether to display the image component
 * @param {React.ReactNode} [props.imageComponent] - Image component to display
 * @returns {React.ReactElement} The learning activity list item component
 */
const LearningActivityListItem = (props) => {
  const mobileViewport = useMobileViewportCheck();

  return mobileViewport ? (
    <LearningActivityMobileListItem {...props} />
  ) : (
    <LearningActivityDesktopListItem {...props} />
  );
};

const LearningActivityDesktopListItem = ({
  title,
  type,
  chips,
  content,
  actions,
  displayImage = false,
  imageComponent,
}) => {
  return (
    <Grid container alignItems="center" className="learning-courselist-item">
      {displayImage ? (
        <Grid item xs={"auto"} className="learning-courselist-item-image">
          {imageComponent}
        </Grid>
      ) : (
        <ItemIcon
          type={type}
          size="small"
          sx={{ marginRight: 2 }}
          className="learning-courselist-icon"
        />
      )}
      <Grid
        container
        item
        xs
        direction="column"
        className="learning-courselist-info"
      >
        <Grid item>
          <Typography
            variant="h3"
            title={title}
            className={"elipssis"}
            sx={{ maxWidth: "700px" }}
          >
            {title}
          </Typography>
        </Grid>
        <Grid item>{content}</Grid>
      </Grid>
      <Grid
        container
        item
        xs={"auto"}
        direction="column"
        sx={{ alignItems: "end" }}
      >
        <Grid item>{chips}</Grid>
        <Grid item>{actions}</Grid>
      </Grid>
    </Grid>
  );
};

const LearningActivityMobileListItem = ({
  title,
  type,
  chips,
  content,
  actions,
  displayImage = false,
  imageComponent,
}) => {
  return (
    <Grid
      item
      container
      direction="column"
      className="learning-courselist-item-mobile"
    >
      <Grid
        item
        sx="auto"
        container
        justifyContent="space-between"
        float="right"
      >
        {displayImage && (
          <Grid item className="learning-courselist-item-image">
            {imageComponent}
          </Grid>
        )}
        <Grid item direction="column">
          {chips}
        </Grid>
      </Grid>

      <Grid item>
        {!displayImage && <ItemIcon type={type} size="small" />}
        <Typography
          variant="h3"
          title={title}
          className={"elipssis"}
          sx={{ maxWidth: "85wv" }}
        >
          {title}
        </Typography>
      </Grid>

      <Grid item>{content}</Grid>

      <Grid xs="auto" item container justifyContent="flex-end">
        {actions}
      </Grid>
    </Grid>
  );
};

export default LearningActivityListItem;
